import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Bell, BellOff, FileText, ParkingCircle, Calendar, Star, 
  AlertCircle, Users, Shield, Pill, Heart, MessageSquare,
  CheckCircle, Settings, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  getNotificationSettings,
  saveNotificationSettings,
  showNotification,
} from '@/services/notificationService';

const ADVANCE_OPTIONS = [
  { value: '5', label: '5 minutes' },
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '1440', label: '1 day' },
  { value: '2880', label: '2 days' },
  { value: '10080', label: '1 week' },
];

// Notification categories
const NOTIFICATION_CATEGORIES = {
  reminders: {
    title: 'Reminders',
    description: 'Document expiration, parking, calendar events',
    icon: Calendar,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    types: [
      {
        key: 'documentExpiring',
        icon: FileText,
        label: 'Document Expiration',
        description: 'Get notified when documents are about to expire',
        advanceKey: 'documents',
        color: 'text-slate-600',
      },
      {
        key: 'parkingExpiring',
        icon: ParkingCircle,
        label: 'Parking Reminders',
        description: 'Get notified before your parking expires',
        advanceKey: 'parking',
        color: 'text-purple-600',
      },
      {
        key: 'calendarEvents',
        icon: Calendar,
        label: 'Calendar Events',
        description: 'Get notified about upcoming events',
        advanceKey: 'calendar',
        color: 'text-blue-600',
      },
      {
        key: 'importantDates',
        icon: Star,
        label: 'Important Dates',
        description: 'Get notified about birthdays, anniversaries, etc.',
        advanceKey: 'dates',
        color: 'text-amber-600',
      },
    ],
  },
  family: {
    title: 'Family & Caregivers',
    description: 'Updates from family members and caregivers',
    icon: Users,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    types: [
      {
        key: 'familyInvitations',
        icon: Users,
        label: 'Family Invitations',
        description: 'When someone invites you to their family network',
        color: 'text-emerald-600',
      },
      {
        key: 'familyUpdates',
        icon: Heart,
        label: 'Family Updates',
        description: 'When a family member updates their emergency info',
        color: 'text-rose-500',
      },
      {
        key: 'caregiverActivity',
        icon: Shield,
        label: 'Caregiver Activity',
        description: 'When a caregiver views or updates your information',
        color: 'text-sky-600',
      },
    ],
  },
  health: {
    title: 'Health & Medical',
    description: 'Medication reminders and health alerts',
    icon: Pill,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    types: [
      {
        key: 'medicationReminders',
        icon: Pill,
        label: 'Medication Reminders',
        description: 'Daily reminders to take your medications',
        color: 'text-rose-600',
      },
      {
        key: 'appointmentReminders',
        icon: Calendar,
        label: 'Appointment Reminders',
        description: 'Upcoming doctor appointments and checkups',
        advanceKey: 'appointments',
        color: 'text-teal-600',
      },
    ],
  },
  system: {
    title: 'System & Security',
    description: 'App updates and security alerts',
    icon: Settings,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    types: [
      {
        key: 'securityAlerts',
        icon: Shield,
        label: 'Security Alerts',
        description: 'Login attempts and security-related events',
        color: 'text-red-600',
      },
      {
        key: 'appUpdates',
        icon: Bell,
        label: 'App Updates',
        description: 'New features and important app updates',
        color: 'text-slate-600',
      },
      {
        key: 'tips',
        icon: MessageSquare,
        label: 'Tips & Suggestions',
        description: 'Helpful tips for using Forget Me Not',
        color: 'text-indigo-600',
      },
    ],
  },
};

const NotificationSettings = () => {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    setSupported(isNotificationSupported());
    setPermission(getNotificationPermission());
    
    // Get existing settings and merge with defaults for new notification types
    const existingSettings = getNotificationSettings();
    const defaultSettings = {
      enabled: false,
      // Reminders
      documentExpiring: true,
      parkingExpiring: true,
      calendarEvents: true,
      importantDates: true,
      // Family
      familyInvitations: true,
      familyUpdates: true,
      caregiverActivity: true,
      // Health
      medicationReminders: true,
      appointmentReminders: true,
      // System
      securityAlerts: true,
      appUpdates: false,
      tips: false,
      // Advance minutes
      advanceMinutes: {
        documents: 1440,
        parking: 30,
        calendar: 60,
        dates: 1440,
        appointments: 1440,
      },
    };
    
    setSettings({ ...defaultSettings, ...existingSettings });
  }, []);

  const handleRequestPermission = async () => {
    setLoading(true);
    const result = await requestNotificationPermission();
    setPermission(getNotificationPermission());
    setLoading(false);

    if (result.granted) {
      toast.success('Notifications enabled!');
      showNotification('Notifications Enabled', {
        body: 'You will now receive reminders for important events.',
      });
    } else {
      toast.error(result.reason || 'Failed to enable notifications');
    }
  };

  const handleToggleEnabled = (enabled) => {
    if (enabled && permission !== 'granted') {
      handleRequestPermission();
      return;
    }

    const newSettings = { ...settings, enabled };
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
    toast.success(enabled ? 'Notifications enabled' : 'Notifications disabled');
  };

  const handleToggleType = (type, enabled) => {
    const newSettings = { ...settings, [type]: enabled };
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  const handleToggleCategory = (categoryKey, enabled) => {
    const category = NOTIFICATION_CATEGORIES[categoryKey];
    const newSettings = { ...settings };
    
    category.types.forEach(type => {
      newSettings[type.key] = enabled;
    });
    
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
    
    toast.success(`${category.title} notifications ${enabled ? 'enabled' : 'disabled'}`);
  };

  const isCategoryEnabled = (categoryKey) => {
    const category = NOTIFICATION_CATEGORIES[categoryKey];
    return category.types.some(type => settings[type.key]);
  };

  const isCategoryFullyEnabled = (categoryKey) => {
    const category = NOTIFICATION_CATEGORIES[categoryKey];
    return category.types.every(type => settings[type.key]);
  };

  const handleAdvanceChange = (type, minutes) => {
    const newSettings = {
      ...settings,
      advanceMinutes: {
        ...settings.advanceMinutes,
        [type]: parseInt(minutes),
      },
    };
    setSettings(newSettings);
    saveNotificationSettings(newSettings);
  };

  const handleTestNotification = () => {
    showNotification('Test Notification', {
      body: 'This is a test notification from Forget Me Not!',
    });
    toast.success('Test notification sent');
  };

  if (!supported) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-amber-700">
            <AlertCircle className="h-5 w-5" />
            <p>Push notifications are not supported in this browser.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) return null;

  return (
    <Card data-testid="notification-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.enabled && permission === 'granted' ? (
              <Bell className="h-5 w-5 text-emerald-600" />
            ) : (
              <BellOff className="h-5 w-5 text-slate-400" />
            )}
            <div>
              <CardTitle>Push Notifications</CardTitle>
              <CardDescription>Get reminders and updates</CardDescription>
            </div>
          </div>
          <Switch
            checked={settings.enabled && permission === 'granted'}
            onCheckedChange={handleToggleEnabled}
            data-testid="notifications-master-toggle"
          />
        </div>
      </CardHeader>

      {permission === 'denied' && (
        <CardContent className="pt-0 pb-4">
          <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-sm text-red-700">
            <p className="font-medium">Notifications are blocked</p>
            <p className="text-red-600 mt-1">
              Please enable notifications in your browser settings for this site.
            </p>
          </div>
        </CardContent>
      )}

      {permission === 'default' && (
        <CardContent className="pt-0 pb-4">
          <Button
            onClick={handleRequestPermission}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Bell className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Requesting...' : 'Enable Notifications'}
          </Button>
        </CardContent>
      )}

      {settings.enabled && permission === 'granted' && (
        <CardContent className="space-y-4 pt-0">
          {/* Notification Categories */}
          {Object.entries(NOTIFICATION_CATEGORIES).map(([categoryKey, category]) => {
            const CategoryIcon = category.icon;
            const isExpanded = expandedCategory === categoryKey;
            const isEnabled = isCategoryEnabled(categoryKey);
            const isFullyEnabled = isCategoryFullyEnabled(categoryKey);
            
            return (
              <div 
                key={categoryKey}
                className="border rounded-xl overflow-hidden"
              >
                {/* Category Header */}
                <div 
                  className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                    isEnabled ? category.bgColor : 'bg-slate-50'
                  }`}
                  onClick={() => setExpandedCategory(isExpanded ? null : categoryKey)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isEnabled ? 'bg-white' : 'bg-slate-100'
                    }`}>
                      <CategoryIcon className={`h-5 w-5 ${isEnabled ? category.color : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{category.title}</p>
                      <p className="text-xs text-slate-500">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={(enabled) => handleToggleCategory(categoryKey, enabled)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`category-toggle-${categoryKey}`}
                    />
                  </div>
                </div>
                
                {/* Category Types (Expanded) */}
                {isExpanded && isEnabled && (
                  <div className="p-4 pt-0 space-y-3 border-t">
                    {category.types.map((type) => {
                      const TypeIcon = type.icon;
                      return (
                        <div key={type.key} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                          <Switch
                            checked={settings[type.key]}
                            onCheckedChange={(enabled) => handleToggleType(type.key, enabled)}
                            className="mt-0.5"
                            data-testid={`notification-toggle-${type.key}`}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <TypeIcon className={`h-4 w-4 ${type.color}`} />
                              <Label className="text-sm font-medium">{type.label}</Label>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">{type.description}</p>
                            
                            {settings[type.key] && type.advanceKey && (
                              <div className="mt-2 flex items-center gap-2">
                                <span className="text-xs text-slate-500">Notify:</span>
                                <Select
                                  value={String(settings.advanceMinutes[type.advanceKey] || 60)}
                                  onValueChange={(value) => handleAdvanceChange(type.advanceKey, value)}
                                >
                                  <SelectTrigger className="h-7 w-32 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {ADVANCE_OPTIONS.map((opt) => (
                                      <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label} before
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Test Notification Button */}
          <div className="pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              data-testid="test-notification-btn"
            >
              <Bell className="h-4 w-4 mr-2" />
              Send Test Notification
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default NotificationSettings;
